import { useQuery } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

/** GET `/wanted/{year}/{month}/shift-requests` — `results[].shifts[]` 항목 */
export type WantedShiftRequestShift = {
  shift_date: string;
  shift: string;
  shifts_table_id: number;
  score: number;
  comment: string;
};

/** GET `/wanted/{year}/{month}/shift-requests` — `results[]` 항목 */
export type WantedShiftRequestResult = {
  nurse_id: string;
  is_submitted: boolean;
  submitted_at: string | null;
  shifts: WantedShiftRequestShift[];
  assignments?: WantedShiftRequestAssignment[];
};

/** `assignments[].source_shifts` / `target_shifts` — `DD`(2자리 일) -> shift code */
export type WantedAssignmentShiftsByDay = Record<string, string | null>;

/** GET `/wanted/{year}/{month}/shift-requests` — `assignments[]` 항목 */
export type WantedShiftRequestAssignment = {
  nurse_id?: string;
  direction?: "inbound" | "outbound" | string;
  start_date?: string;
  end_date?: string | null;
  /** 호환: camelCase */
  startDate?: string;
  endDate?: string | null;
  period_start_day?: number;
  period_end_day?: number;
  reason: string;
  source_group_id: string;
  source_group_name?: string;
  target_group_id: string;
  target_group_name?: string;
  source_schedule_id: string;
  source_shifts: WantedAssignmentShiftsByDay;
  target_schedule_id: string;
  target_shifts: WantedAssignmentShiftsByDay;
};

/** GET `/wanted/{year}/{month}/shift-requests` 응답 본문 */
export type WantedShiftRequestsResponse = {
  results: WantedShiftRequestResult[];
  /** 파견/이동 등 기간 배정(소스·타겟 시프트 포함) */
  assignments?: WantedShiftRequestAssignment[];
};

/**
 * GET `/wanted/{year}/{month}/shift-requests`
 */
export function useWantedShiftRequestsQuery({
  year,
  month,
  groupId,
  enabled = true,
}: {
  year: number;
  month: number;
  groupId?: string | null;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
    queryFn: async () => {
      const params: { group_id?: string } = {};
      if (groupId && groupId.trim() !== "" && groupId !== "null") {
        params.group_id = groupId;
      }
      return await httpClient.get<WantedShiftRequestsResponse>(
        `/wanted/${year}/${month}/shift-requests`,
        Object.keys(params).length > 0 ? { params } : undefined
      );
    },
    enabled: enabled && !!year && !!month,
    staleTime: 60 * 1000,
  });
}
